import { useTranslation } from "react-i18next";

import { useOptimizedMachineTimes } from "#modules/optimizations/view/useOptimizedMachineTimes";
import { useOptimizedMaterialCosts } from "#modules/optimizations/view/useOptimizedMaterialCosts";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { computeQuoteRequestTotals } from "#modules/quoteRequests/domain/computeQuoteRequestTotals";
import { QuoteRequestDto } from "#modules/quoteRequests/infra/fetchQuoteRequest.api";
import { marginPercentOfCost } from "#shared/helpers/prices";
import { Flex } from "#shared/view/components/Flex";
import { InlineLoader } from "#shared/view/components/InlineLoader";
import { Paper } from "#shared/view/components/Paper";
import { Text } from "#shared/view/components/Text";
import { formatPrice, ProcessStep } from "shared";

type Props = {
  quoteRequest: QuoteRequestDto | null | undefined;
  quoteRequestProcessSteps: ProcessStep[];
  quoteRequestLines: QuoteRequestLineDto[];
  salesMarginRatio: number;
  discountRatio: number;
  isLoading: boolean;
};

export const BottomPriceDisplay = ({
  quoteRequest,
  quoteRequestProcessSteps,
  quoteRequestLines,
  salesMarginRatio,
  discountRatio,
  isLoading,
}: Props) => {
  const { t } = useTranslation();
  const optimizedMachineTimes = useOptimizedMachineTimes(quoteRequest?.id);
  const optimizedMaterialCosts = useOptimizedMaterialCosts(quoteRequest?.id);

  if (!quoteRequest) {
    return null;
  }

  const { totalBeforeSalesMarginAndDiscount, totalPrice } = computeQuoteRequestTotals({
    quoteRequest,
    quoteRequestProcessSteps,
    quoteRequestLines,
    salesMarginRatio,
    discountRatio,
    optimizedMachineTimes,
    optimizedMaterialCosts,
  });
  const { cost } = totalBeforeSalesMarginAndDiscount;
  const marginPercent = marginPercentOfCost(totalPrice, cost);
  const bottomPriceCanBeDisplayed = hasAtLeastOnePrediction(quoteRequestLines);

  return (
    <Flex gap="0" justify="end">
      <Paper
        bd={"1px solid var(--mantine-color-violet-5)"}
        p="xxs"
        radius="xs"
        shadow="none"
        w={"max-content"}
      >
        <Flex gap="xs">
          <Text fw={700}>{t("quoteRequestLine.totalCostLabel")} :</Text>

          {isLoading ? (
            <InlineLoader color="violet" size="xs" />
          ) : (
            <Text fw={700}>
              {bottomPriceCanBeDisplayed
                ? `${formatPrice(cost)} ${t("quoteRequestLine.excludingTax")}`
                : "-"}
            </Text>
          )}
        </Flex>

        <Flex gap="xs">
          <Text fw={700}>{t("quoteRequestLine.totalPriceLabel")} :</Text>

          {isLoading ? (
            <InlineLoader color="violet" size="xs" />
          ) : (
            <Text fw={700}>
              {bottomPriceCanBeDisplayed
                ? `${formatPrice(totalPrice)} ${t(
                    "quoteRequestLine.excludingTax",
                  )} (${marginPercent > 0 ? "+" : ""}${marginPercent}%)`
                : "-"}
            </Text>
          )}
        </Flex>
      </Paper>
    </Flex>
  );
};

function hasAtLeastOnePrediction(quoteRequestLines: { processSteps: object[] }[] | undefined) {
  return !!quoteRequestLines?.some((quoteRequestLine) => !!quoteRequestLine.processSteps.length);
}
